import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowPathIcon, CheckCircleIcon, EyeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useI18n } from '../i18n/I18nProvider';
import { buildPracticeHistoryPath } from '../lib/practiceAttempts';
import { formatIeltsBand, ieltsBandFromAttempt } from '../lib/ieltsSpeaking';

const labels = {
  dictation: 'Dictation Loop',
  meaning_sprint: '7-Second Meaning',
  sudden_sprint: 'Lightning Challenge',
  pronunciation: 'Pronunciation Practice',
  adaptive_reading: 'Adaptive Reading',
  ielts_speaking: 'IELTS Speaking Practice',
};

export default function StudentPracticeAssignmentsModal({ isOpen, onClose, classId, student, onChanged }) {
  const { t } = useI18n();
  const [practices, setPractices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (quiet = false) => {
    if (!isOpen || !classId || !student?._id) return;
    if (quiet) setRefreshing(true); else setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/admin/classes/${classId}/students/${student._id}/practice-assignments`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || t('The practice list could not be loaded.'));
      setPractices(Array.isArray(data.practices) ? data.practices : []);
    } catch (loadError) {
      setError(loadError.message || t('The practice list could not be loaded.'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [classId, isOpen, student?._id, t]);

  useEffect(() => { load(); }, [load]);
  if (!isOpen) return null;

  const archive = async (assignmentId) => {
    try {
      const response = await fetch(`/api/admin/practice-assignments/${assignmentId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (!response.ok) throw new Error(t('The practice could not be removed.'));
      await load(true);
      onChanged?.();
    } catch (archiveError) {
      setError(archiveError.message || t('The practice could not be removed.'));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-5">
      <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
        <header className="flex items-start justify-between border-b px-5 py-4 sm:px-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-950">{t('Student practices')}</h2>
            <p data-i18n-skip className="mt-1 text-sm text-gray-500">{student?.username}{student?.nickname ? ` (${student.nickname})` : ''}</p>
          </div>
          <div className="flex items-center gap-2"><button type="button" onClick={() => load(true)} disabled={refreshing} title={t('Refresh')} className="rounded border border-gray-300 p-2 text-gray-600"><ArrowPathIcon className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} /></button><button type="button" onClick={onClose} title={t('Close')} className="rounded p-2 text-gray-500"><XMarkIcon className="h-6 w-6" /></button></div>
        </header>
        <div className="overflow-y-auto px-5 py-5 sm:px-6">
          {error && <div className="mb-4 rounded border border-rose-200 bg-rose-50 px-4 py-3 text-rose-800">{error}</div>}
          {loading ? (
            <div className="py-12 text-center text-gray-500">{t('Loading...')}</div>
          ) : practices.length ? (
            <div className="divide-y divide-gray-200 border-y border-gray-200">
              {practices.map((item) => {
                const assignment = item.assignment || {};
                const latest = item.latest_attempt || {};
                const isIelts = assignment.practice_type === 'ielts_speaking';
                const predictedBand = isIelts ? ieltsBandFromAttempt(latest) : null;
                  const statusLabel = item.status === 'completed'
                    ? 'Completed'
                    : item.status === 'in_progress'
                      ? 'In progress'
                      : item.status === 'overdue' ? 'Overdue' : item.status === 'upcoming' ? 'Upcoming' : 'Not started';
                return (
                  <div key={item.id} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <CheckCircleIcon className={`h-5 w-5 ${item.status === 'completed' ? 'text-emerald-600' : 'text-gray-400'}`} />
                        <p data-i18n-skip className="font-semibold text-gray-950">{assignment.title}</p>
                      </div>
                      <p className="mt-1 pl-7 text-sm text-gray-500">
                        {t(labels[assignment.practice_type] || 'Practice')} · {t(statusLabel)} · {t('{count} attempts', { count: item.attempt_count || 0 })}
                        {isIelts
                          ? predictedBand != null ? ` · ${t('Predicted band')} ${formatIeltsBand(predictedBand)}` : ''
                          : latest.accuracy != null ? ` · ${Math.round(Number(latest.accuracy) * 100)}%` : ''}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={buildPracticeHistoryPath(assignment.id, 'admin')}
                        className="inline-flex items-center gap-2 rounded border border-indigo-200 bg-white px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50"
                      >
                        <EyeIcon className="h-4 w-4" aria-hidden="true" />
                        {t('View report')}
                      </Link>
                      <button type="button" onClick={() => archive(assignment.id)} className="rounded border border-red-200 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50">{t('Archive')}</button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="py-12 text-center text-gray-500">{t('No assigned practice yet.')}</p>
          )}
        </div>
      </div>
    </div>
  );
}
